<!-- Mobile Layout Container -->
<div class="mobile-layout">
    <!-- PWA Install Banner -->
    <div class="install-banner" *ngIf="showInstallButton && !isStandalone">
        <div class="install-content">
            <mat-icon>download</mat-icon>
            <span>Install DPT Mobile for quick access</span>
        </div>
        <div class="install-actions">
            <button class="install-btn" (click)="installPwa()">Install</button>
            <button class="dismiss-btn" (click)="dismissInstall()">
                <mat-icon>close</mat-icon>
            </button>
        </div>
    </div>

    <!-- Main Content Area -->
    <main class="mobile-content">
        <router-outlet></router-outlet>
    </main>

    <!-- Bottom Navigation Bar -->
    <nav class="mobile-nav" [class.standalone]="isStandalone">
        <!-- Orders Tab -->
        <button 
            class="nav-tab"
            [class.active]="activeTab === 'orders'"
            (click)="goToOrders()"
        >
            <div class="tab-icon">
                <mat-icon [svgIcon]="activeTab === 'orders' ? 'heroicons_solid:clipboard-document-list' : 'heroicons_outline:clipboard-document-list'"></mat-icon>
            </div>
            <span class="tab-label">Orders</span>
        </button>

        <!-- Delivery Tab - Only for admin/production users -->
        <button
            *ngIf="showDeliveryTab"
            class="nav-tab"
            [class.active]="activeTab === 'delivery'"
            (click)="goToDelivery()"
        >
            <div class="tab-icon">
                <!--
                    Badge shows the number of pending deliveries
                    (server-side count from `getDeliveryOrders`).
                    Mirrors the chat/email unread pattern — same warn
                    color, same "99+" cap. Hidden when 0 so the icon
                    reads as quiet/empty when nothing's waiting.
                -->
                <mat-icon
                    [svgIcon]="activeTab === 'delivery' ? 'heroicons_solid:truck' : 'heroicons_outline:truck'"
                    [matBadge]="pendingDeliveryCount > 0 ? formattedDeliveryCount : null"
                    matBadgeColor="warn"
                    matBadgeSize="small"
                ></mat-icon>
            </div>
            <span class="tab-label">Delivery</span>
        </button>

        <!-- Chat Tab (operations layout) - Admin / Production surface Chat
             as a primary bottom tab, positioned right after Delivery so
             the order reads Orders, Delivery, Chat, Notes. -->
        <button
            *ngIf="usesOpsLayout"
            class="nav-tab"
            [class.active]="activeTab === 'chat'"
            (click)="goToChat()"
        >
            <div class="tab-icon">
                <mat-icon
                    [svgIcon]="activeTab === 'chat' ? 'heroicons_solid:chat-bubble-left-right' : 'heroicons_outline:chat-bubble-left-right'"
                    [matBadge]="unreadChatCount > 0 ? formattedUnreadCount : null"
                    matBadgeColor="warn"
                    matBadgeSize="small"
                ></mat-icon>
            </div>
            <span class="tab-label">Chat</span>
        </button>

        <!-- Email Tab - internal staff only (hidden for customers).
             Sales / Trader and Admin / Production both move Email into the
             overflow sheet, so it's hidden from the bottom strip for those
             layouts. -->
        <button 
            *ngIf="showEmailTab && !usesQuotingLayout && !usesOpsLayout"
            class="nav-tab"
            [class.active]="activeTab === 'email'"
            (click)="goToEmail()"
        >
            <div class="tab-icon">
                <mat-icon 
                    [matBadge]="unreadEmailCount > 0 ? (unreadEmailCount > 99 ? '99+' : unreadEmailCount) : null"
                    matBadgeColor="warn"
                    matBadgeSize="small"
                >mail</mat-icon>
            </div>
            <span class="tab-label">Email</span>
        </button>

        <!-- Pricing Tab - quoting roles. Hidden from the bottom strip for
             the Admin / Production ops layout, where it moves into the
             overflow sheet. -->
        <button
            *ngIf="showPricingTab && !usesOpsLayout"
            class="nav-tab"
            [class.active]="activeTab === 'pricing'"
            (click)="goToPricing()"
        >
            <div class="tab-icon">
                <mat-icon>calculate</mat-icon>
            </div>
            <span class="tab-label">Pricing</span>
        </button>

        <button
            *ngIf="showAiQuoteTab"
            type="button"
            class="nav-tab"
            [class.active]="activeTab === 'aiQuote'"
            (click)="goToAiQuote()"
        >
            <div class="tab-icon">
                <mat-icon>{{ activeTab === 'aiQuote' ? 'record_voice_over' : 'mic' }}</mat-icon>
            </div>
            <span class="tab-label">AI Quote</span>
        </button>

        <!--
            Mockup Studio Tab - promoted to a first-class bottom tab for
            the Sales / Trader quoting layout (it lives in the overflow
            sheet for every other role). Uses the same custom "tee with
            sparkles" mark as the overflow item since Material has no
            shirt+stars glyph.
        -->
        <button
            *ngIf="usesQuotingLayout && showMockupTab"
            class="nav-tab"
            [class.active]="activeTab === 'mockup'"
            (click)="goToMockup()"
        >
            <div class="tab-icon">
                <svg class="mockup-tee-ico" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                        d="M15.5 3a3.5 3.5 0 0 1-7 0L4 4.5 2 8.5 5 11v10h14V11l3-2.5-2-4z"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linejoin="round"
                        stroke-linecap="round"
                    />
                    <path class="mockup-tee-ico__spark" d="M11.6 11.4l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
                    <path class="mockup-tee-ico__spark" d="M15.4 14.6l.42 1.05 1.05.42-1.05.42-.42 1.05-.42-1.05-1.05-.42 1.05-.42z" />
                </svg>
            </div>
            <span class="tab-label">Mockup</span>
        </button>

        <!--
            Quotes & Notes Tab - Sales / Trader only.
            Dedicated home for saved pricing quotes + notepad snapshots
            (the same records as the desktop quick-chat Archive panel),
            so Sales reps can find quotes they saved without digging
            through Chat.
        -->
        <button
            *ngIf="showQuotesTab || usesOpsLayout"
            class="nav-tab"
            [class.active]="activeTab === 'quotes'"
            (click)="goToQuotes()"
        >
            <div class="tab-icon">
                <mat-icon>request_quote</mat-icon>
            </div>
            <span class="tab-label">Quotes</span>
        </button>

        <!-- Chat Tab (default layout) - hidden for the Sales / Trader
             quoting layout (moves to overflow) and for the Admin /
             Production ops layout (which renders its own Chat tab right
             after Delivery, above). -->
        <button 
            *ngIf="!usesQuotingLayout && !usesOpsLayout"
            class="nav-tab"
            [class.active]="activeTab === 'chat'"
            (click)="goToChat()"
        >
            <div class="tab-icon">
                <mat-icon 
                    [svgIcon]="activeTab === 'chat' ? 'heroicons_solid:chat-bubble-left-right' : 'heroicons_outline:chat-bubble-left-right'"
                    [matBadge]="unreadChatCount > 0 ? formattedUnreadCount : null"
                    matBadgeColor="warn"
                    matBadgeSize="small"
                ></mat-icon>
            </div>
            <span class="tab-label">Chat</span>
        </button>

        <!-- Additional destinations live directly in the horizontally
             swipeable nav strip instead of a separate overflow popover. -->
        <button
            *ngIf="usesQuotingLayout"
            type="button"
            class="nav-tab"
            [class.active]="activeTab === 'chat'"
            (click)="goToChat()"
        >
            <div class="tab-icon">
                <mat-icon
                    [svgIcon]="activeTab === 'chat' ? 'heroicons_solid:chat-bubble-left-right' : 'heroicons_outline:chat-bubble-left-right'"
                    [matBadge]="unreadChatCount > 0 ? formattedUnreadCount : null"
                    matBadgeColor="warn"
                    matBadgeSize="small"
                ></mat-icon>
            </div>
            <span class="tab-label">Chat</span>
        </button>

        <button
            *ngIf="(usesQuotingLayout || usesOpsLayout) && showEmailTab"
            type="button"
            class="nav-tab"
            [class.active]="activeTab === 'email'"
            (click)="goToEmail()"
        >
            <div class="tab-icon">
                <mat-icon
                    [matBadge]="unreadEmailCount > 0 ? (unreadEmailCount > 99 ? '99+' : unreadEmailCount) : null"
                    matBadgeColor="warn"
                    matBadgeSize="small"
                >mail</mat-icon>
            </div>
            <span class="tab-label">Email</span>
        </button>

        <button
            *ngIf="usesOpsLayout && showPricingTab"
            type="button"
            class="nav-tab"
            [class.active]="activeTab === 'pricing'"
            (click)="goToPricing()"
        >
            <div class="tab-icon">
                <mat-icon>calculate</mat-icon>
            </div>
            <span class="tab-label">Pricing</span>
        </button>

        <button
            *ngIf="showMockupTab && !usesQuotingLayout"
            type="button"
            class="nav-tab"
            [class.active]="activeTab === 'mockup'"
            (click)="goToMockup()"
        >
            <div class="tab-icon">
                <svg class="mockup-tee-ico" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                        d="M15.5 3a3.5 3.5 0 0 1-7 0L4 4.5 2 8.5 5 11v10h14V11l3-2.5-2-4z"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linejoin="round"
                        stroke-linecap="round"
                    />
                    <path class="mockup-tee-ico__spark" d="M11.6 11.4l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
                    <path class="mockup-tee-ico__spark" d="M15.4 14.6l.42 1.05 1.05.42-1.05.42-.42 1.05-.42-1.05-1.05-.42 1.05-.42z" />
                </svg>
            </div>
            <span class="tab-label">Mockup</span>
        </button>

        <button
            type="button"
            class="nav-tab"
            [class.active]="activeTab === 'notes'"
            (click)="goToNewNote()"
        >
            <div class="tab-icon">
                <mat-icon>note_add</mat-icon>
            </div>
            <span class="tab-label">New Note</span>
        </button>

        <button
            type="button"
            class="nav-tab nav-tab--danger"
            (click)="signOut()"
        >
            <div class="tab-icon">
                <mat-icon>logout</mat-icon>
            </div>
            <span class="tab-label">Sign Out</span>
        </button>
    </nav>
</div>
